import { useEffect } from 'react';
import { Button, Col, Image, Row, Stack } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import optainIdentifyRetinaImg from 'src/assets/img/optain-hint2.jpg';
import optainTakePhotoImg from 'src/assets/img/optain-hint3.jpg';
import patientPositionImg from 'src/assets/img/patient-position.png';
import { ExamStepEnum } from 'src/services/offline/db';
import { setOrderLastStepIDB } from 'src/services/offline/outboxClient';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { I18NextNS } from 'src/models/Language.model';

import useCaptureImage from 'src/hooks/camera/useCaptureImage';

import PatientInfoWithControls from './custom_components/PatientInfoWithControls';

const ExamHint = () => {
  const history = useHistory();

  const { session, resetSession, isCanceling } = useRemoteBoundStore();

  useEffect(() => {
    if (!session.orderId) {
      return;
    }
    setOrderLastStepIDB(session.orderId, ExamStepEnum.EXAM_HINT);
  }, [session.orderId]);

  const handlePrevious = () => {
    resetSession();
    history.push('/session-form');
  };

  const { captureBothImages } = useCaptureImage({
    watchConnectionIssues: false,
  });

  const handleStartImaging = () => {
    history.push('/session-preview');
    captureBothImages();
  };

  const { t } = useTranslation(I18NextNS.PAGES);

  return (
    <div className="d-flex flex-column h-100">
      <div className="flex-fill">
        <PatientInfoWithControls />
        <Row data-test="patient-positioning-instructions">
          <Col>
            <Image
              src={patientPositionImg}
              alt={t('newExam.examHint.stepOne.imgAlt')}
              className="w-100"
            />
            <h5 className="text-primary mt-2 fw-bold">
              {t('newExam.examHint.stepOne.title')}
            </h5>
            <Trans
              i18nKey="newExam.examHint.stepOne.desc"
              ns="pages"
              components={{ p: <p /> }}
            />
          </Col>
          <Col>
            <Image
              src={optainIdentifyRetinaImg}
              alt={t('newExam.examHint.stepTwo.imgAlt')}
              className="w-100"
            />
            <h5 className="text-primary mt-2 fw-bold">
              {t('newExam.examHint.stepTwo.title')}
            </h5>
            <Trans
              i18nKey="newExam.examHint.stepTwo.desc"
              ns="pages"
              components={{ p: <p /> }}
            />
          </Col>
          <Col>
            <Image
              src={optainTakePhotoImg}
              alt={t('newExam.examHint.stepThree.imgAlt')}
              className="w-100"
            />
            <h5 className="text-primary mt-2 fw-bold">
              {t('newExam.examHint.stepThree.title')}
            </h5>
            <Trans
              i18nKey="newExam.examHint.stepThree.desc"
              ns="pages"
              components={{ p: <p /> }}
            />
          </Col>
        </Row>
      </div>

      <Stack
        className="justify-content-end mb-5"
        direction="horizontal"
        gap={4}
      >
        <Button
          variant="primary-100"
          onClick={handlePrevious}
          data-test="previous-button"
          data-track="exam.exam-hint.previous"
          data-observe
        >
          {t('newExam.previous')}
        </Button>
        <Button
          variant="primary"
          onClick={handleStartImaging}
          disabled={isCanceling}
          data-test="start-imaging-button"
          data-track="exam.exam-hint.start-imaging"
          data-observe
        >
          {isCanceling
            ? t('newExam.awaitingCamera')
            : t('newExam.startImaging')}
        </Button>
      </Stack>
    </div>
  );
};

export default ExamHint;
